import React, { createContext, useContext, useEffect, useState } from "react";
import { staffApi, STAFF_TOKEN_KEY, STAFF_USER_KEY, getDeviceId } from "./staffApi";

const StaffAuthCtx = createContext(null);

/**
 * Staff auth context.
 * Iter 152 · Persistent-login strategy:
 *   Field staff work long shifts and can't be forced to re-authenticate every
 *   time they close the tab or Chrome kills the background page. Tokens + user
 *   are stored in localStorage so sessions survive browser restarts. The
 *   backend TTL / logout endpoint remain the source of truth for revocation.
 *   `device_id` also stays in localStorage.
 */
export function StaffAuthProvider({ children }) {
  const [staff, setStaff] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // One-time migration: if a legacy sessionStorage token is present (from
    // the pre-Iter-152 short-lived-session era), promote it to localStorage
    // so this login doesn't get bounced.
    const sessionTok  = sessionStorage.getItem(STAFF_TOKEN_KEY);
    const sessionUser = sessionStorage.getItem(STAFF_USER_KEY);
    if (sessionTok && !localStorage.getItem(STAFF_TOKEN_KEY)) {
      localStorage.setItem(STAFF_TOKEN_KEY, sessionTok);
      if (sessionUser) localStorage.setItem(STAFF_USER_KEY, sessionUser);
    }
    sessionStorage.removeItem(STAFF_TOKEN_KEY);
    sessionStorage.removeItem(STAFF_USER_KEY);

    const raw   = localStorage.getItem(STAFF_USER_KEY);
    const token = localStorage.getItem(STAFF_TOKEN_KEY);
    if (raw && token) {
      try { setStaff(JSON.parse(raw)); } catch { /* ignore */ }
    }
    setReady(true);
  }, []);

  /** Legacy OTP login — kept for existing UI paths. */
  const login = async (phone, otp) => {
    const device_id = getDeviceId();
    const r = await staffApi.post("/public/staff/login/verify", { phone, otp, device_id });
    return storeSession(r.data.token, r.data.staff);
  };

  /**
   * Iter 46 · used by password / PIN / biometric flows. Accepts a token
   * + user object already validated server-side.
   */
  const loginWithToken = (token, staffObj) => storeSession(token, staffObj);

  const storeSession = (token, staffObj) => {
    localStorage.setItem(STAFF_TOKEN_KEY, token);
    localStorage.setItem(STAFF_USER_KEY, JSON.stringify(staffObj));
    setStaff(staffObj);
    return staffObj;
  };

  const logout = async () => {
    try {
      if (localStorage.getItem(STAFF_TOKEN_KEY)) {
        await staffApi.post("/staff-app/logout");
        await staffApi.post("/mobile-auth/logout").catch(() => {});
      }
    } catch { /* ignore — always clear local state */ }
    localStorage.removeItem(STAFF_TOKEN_KEY);
    localStorage.removeItem(STAFF_USER_KEY);
    setStaff(null);
  };

  return (
    <StaffAuthCtx.Provider value={{ staff, login, loginWithToken, logout, ready }}>
      {children}
    </StaffAuthCtx.Provider>
  );
}

export function useStaffAuth() {
  const ctx = useContext(StaffAuthCtx);
  if (!ctx) throw new Error("useStaffAuth must be used inside StaffAuthProvider");
  return ctx;
}
